import React, { useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  CreditCard,
  Globe2,
  Handshake,
  Info,
  Lightbulb,
  Link,
  Mail,
  Phone,
  Rocket,
  Tags,
  InfoItem,
  NoticeBox,
  SectionHeader,
} from "../../ui";

// Shared labels. Every text is { ar, en }; plain strings are the same in both languages.
const LABELS = {
  frequency: { ar: "تكرار التحويل", en: "Transfer frequency" },
  percentage: { ar: "نسبة التحويل", en: "Transfer percentage" },
  minimum: { ar: "الحد الأدنى", en: "Minimum" },
  processing: { ar: "مدة التنفيذ", en: "Processing time" },
  taxInvoice: { ar: "الفاتورة الضريبية", en: "Tax invoice" },
  transferName: { ar: "اسم التحويل", en: "Transfer name" },
  invoiceIssuance: { ar: "إصدار الفاتورة", en: "Invoice issuance" },
  periods: { ar: "فترات الحوالة", en: "Transfer periods" },
  contacts: { ar: "إيميلات التواصل", en: "Contact emails" },
  portalButton: { ar: "البورتال (اضغط هنا)", en: "Portal (click here)" },
};

const PLATFORMS = [
  {
    id: "jahez",
    logo: { ar: "جـ", en: "J" },
    name: { ar: "جاهز", en: "Jahez" },
    subtitle: { ar: "4 مرات شهرياً · 3 أيام عمل", en: "4 times monthly · 3 business days" },
    portalText: { ar: "بوابة جاهز للشركاء", en: "Jahez partner portal" },
    portalUrl: "https://portal.jahez.net/restaurant-login.htm?action=logout",
    meta: [
      { label: LABELS.frequency, value: { ar: "4 مرات شهرياً", en: "4 times monthly" }, highlight: true },
      { label: LABELS.percentage, value: { ar: "75% من صافي المبيعات", en: "75% of net sales" }, highlight: true },
      { label: LABELS.minimum, value: { ar: "300 ريال", en: "SAR 300" } },
      { label: LABELS.processing, value: { ar: "3 أيام عمل", en: "3 business days" } },
      { label: LABELS.taxInvoice, value: { ar: "فاتورة واحدة شهرياً", en: "One invoice monthly" } },
      { label: LABELS.transferName, value: "JAHEZ INTERNATIONAL CO. FOR INFORMA", small: true },
    ],
    periods: [
      { ar: "الأسبوع الأول: 1 – 10", en: "First week: 1 – 10" },
      { ar: "الأسبوع الثاني: 11 – 17", en: "Second week: 11 – 17" },
      { ar: "الأسبوع الثالث: 18 – 24", en: "Third week: 18 – 24" },
      { ar: "التحويل النهائي: 1 – آخر الشهر", en: "Final transfer: 1 – end of month" },
    ],
    note: {
      variant: "success",
      icon: Lightbulb,
      last: true,
      text: {
        ar: "إذا كان الرصيد أقل من 300 ريال، يُرحّل لنهاية الشهر. التحويل النهائي يشمل المستحقات المتبقية بعد خصم العمولات والتسويات.",
        en: "If the balance is less than SAR 300, it is carried over to the end of the month. The final transfer includes remaining dues after deducting commissions and settlements.",
      },
    },
    contacts: [
      { icon: Handshake, text: { ar: "الشركاء: pr@jahez.net", en: "Partners: pr@jahez.net" } },
      { icon: CreditCard, text: { ar: "المالية: fs-restaurants@jahez.net", en: "Finance: fs-restaurants@jahez.net" } },
    ],
  },
  {
    id: "ninja",
    logo: { ar: "نـ", en: "N" },
    name: { ar: "نينجا", en: "Ninja" },
    subtitle: { ar: "مرتان شهرياً · 7 أيام عمل", en: "Twice monthly · 7 business days" },
    portalText: { ar: "بوابة نينجا للشركاء", en: "Ninja partner portal" },
    portalUrl: "https://restaurant-portal.ananinja.com/ar/login",
    meta: [
      { label: LABELS.frequency, value: { ar: "مرتان شهرياً", en: "Twice monthly" }, highlight: true },
      { label: LABELS.processing, value: { ar: "7 أيام عمل", en: "7 business days" } },
      { label: LABELS.taxInvoice, value: { ar: "4 فواتير شهرياً (أسبوعية)", en: "4 invoices monthly (weekly)" } },
      { label: LABELS.transferName, value: { ar: "شركة نينجا فود", en: "Ninja Food Company" } },
    ],
    periods: [
      { ar: "التحويل الأول: 10 – 15", en: "First transfer: 10 – 15" },
      { ar: "التحويل الثاني: 20 – 25", en: "Second transfer: 20 – 25" },
    ],
    contacts: [
      { icon: Handshake, text: { ar: "الدعم: fsupport@ananinja.com", en: "Support: fsupport@ananinja.com" } },
      { icon: CreditCard, text: { ar: "المالية: billing.food@ananinja.com", en: "Finance: billing.food@ananinja.com" } },
      { icon: Phone, text: "966112096007+" },
    ],
  },
  {
    id: "marsool",
    logo: { ar: "مـ", en: "M" },
    name: { ar: "مرسول", en: "Mrsool" },
    subtitle: { ar: "مرة واحدة شهرياً · 15 يوم عمل", en: "Once monthly · 15 business days" },
    portalText: { ar: "بوابة مرسول للشركاء", en: "Mrsool partner portal" },
    portalUrl: "https://business.mrsool.co/login/",
    meta: [
      { label: LABELS.frequency, value: { ar: "مرة واحدة شهرياً", en: "Once monthly" }, highlight: true },
      { label: LABELS.processing, value: { ar: "15 يوم عمل", en: "15 business days" } },
      { label: LABELS.taxInvoice, value: { ar: "فاتورة واحدة شهرياً", en: "One invoice monthly" } },
      { label: LABELS.transferName, value: "MARSOOL" },
    ],
    periods: [{ ar: "يغطي شهراً ميلادياً كاملاً", en: "Covers a full calendar month" }],
    contacts: [
      { icon: Handshake, text: { ar: "الشركاء: partner.care@mrsool.co", en: "Partners: partner.care@mrsool.co" } },
      { icon: CreditCard, text: { ar: "المالية: collection@mrsool.co", en: "Finance: collection@mrsool.co" } },
    ],
  },
  {
    id: "hungerstation",
    logo: { ar: "هـ", en: "H" },
    name: { ar: "هنقرستيشن", en: "HungerStation" },
    subtitle: { ar: "مرتان شهرياً · 7 أيام عمل", en: "Twice monthly · 7 business days" },
    portalText: { ar: "بوابة هنقرستيشن للشركاء", en: "HungerStation partner portal" },
    portalUrl: "https://partner-app.hungerstation.com/login",
    meta: [
      { label: LABELS.frequency, value: { ar: "مرتان شهرياً", en: "Twice monthly" }, highlight: true },
      { label: LABELS.processing, value: { ar: "7 أيام عمل", en: "7 business days" } },
      { label: LABELS.taxInvoice, value: { ar: "فاتورة واحدة شهرياً", en: "One invoice monthly" } },
    ],
    periodsTitle: { ar: "فترات الحوالة التفصيلية", en: "Detailed transfer periods" },
    periods: [
      { ar: "الفترة الأولى: 1–15 ← التحويل من 22–25", en: "First period: 1–15 ← transfer from 22–25" },
      {
        ar: "الفترة الثانية: 16–آخر الشهر ← التحويل 7–9 (تقفيلة الشهر السابق)",
        en: "Second period: 16–end of month ← transfer 7–9 (previous month closing)",
      },
    ],
    contacts: [{ icon: Handshake, text: "help@hungerstation.com" }],
  },
  {
    id: "keeta",
    logo: { ar: "كـ", en: "K" },
    name: { ar: "كيتا — Keeta", en: "Keeta" },
    subtitle: { ar: "4 مرات شهرياً · 3 أيام عمل", en: "4 times monthly · 3 business days" },
    portalText: { ar: "بوابة كيتا للشركاء", en: "Keeta partner portal" },
    portalUrl:
      "https://passport.mykeeta.com/pc/login?service=merchants&locale=ar&region=SA&cityId=1150000104&token_id=aMJ4hs8U7Dhr-3d6TPvghA&joinkey=1101467_-488047619&backurl=https%3A%2F%2Fmerchant.mykeeta.com%2Fm%2Fweb%2Fapp%2Ffinance%3Flocale%3Dar%23%2Fmain%2Fdownload",
    meta: [
      { label: LABELS.frequency, value: { ar: "4 مرات شهرياً", en: "4 times monthly" }, highlight: true },
      { label: LABELS.processing, value: { ar: "3 أيام عمل", en: "3 business days" } },
      { label: LABELS.taxInvoice, value: { ar: "فاتورة لكل فرع شهرياً", en: "One invoice per branch monthly" } },
      { label: LABELS.transferName, value: { ar: "كيتا – Keeta", en: "Keeta" } },
    ],
    periods: ["1 – 7", "8 – 14", "15 – 21", { ar: "22 – آخر الشهر", en: "22 – end of month" }],
    note: {
      variant: "primary",
      icon: Lightbulb,
      text: {
        ar: "إذا صادف التحويل نهاية الشهر، يشمل حتى اليوم الأخير قبل يوم الثلاثاء من الشهر الجديد.",
        en: "If the transfer falls at the end of the month, it includes up to the last day before Tuesday of the new month.",
      },
    },
    contacts: [{ icon: Handshake, text: "Brand@keetainc.com" }],
  },
  {
    id: "toyou",
    logo: { ar: "تـ", en: "T" },
    name: { ar: "تويو", en: "ToYou" },
    subtitle: { ar: "يوم 10 من كل شهر", en: "The 10th day of each month" },
    portalText: { ar: "بوابة تويو للشركاء", en: "ToYou partner portal" },
    portalUrl: "https://merchant.toyou.io/#/login",
    meta: [
      {
        label: LABELS.invoiceIssuance,
        value: { ar: "يوم 10 من كل شهر", en: "The 10th day of each month" },
        highlight: true,
      },
      { label: LABELS.transferName, value: { ar: "ارام ميم", en: "Aram Meem" } },
    ],
    contacts: [
      { icon: Handshake, text: "merchantcare@toyou.io" },
      { icon: CreditCard, text: "billing@toyou.io" },
    ],
  },
  {
    id: "thechefz",
    logo: { ar: "شـ", en: "C" },
    name: { ar: "ذا شيفز", en: "TheChefz" },
    subtitle: { ar: "راجع البورتال", en: "Check the portal" },
    portalText: { ar: "بوابة ذا شيفز للشركاء", en: "TheChefz partner portal" },
    portalUrl: "https://restaurant-hub.thechefz.co/sign-in/login",
    note: {
      variant: "primary",
      icon: Info,
      last: true,
      text: {
        ar: "يُرجى مراجعة بورتال ذا شيفز للاطلاع على تفاصيل التحويل والمدفوعات.",
        en: "Please check TheChefz portal for transfer and payment details.",
      },
    },
  },
];

const localize = (value, lang) =>
  typeof value === "string" ? value : value[lang === "ar" ? "ar" : "en"];

const MiniTitle = ({ icon: Icon, children }) => (
  <div className="ui-guideline-mini-title">
    <Icon className="guideline-inline-icon" size={16} />
    {children}
  </div>
);

const PlatformCard = ({ platform, lang, collapsed, onToggle }) => {
  const t = (value) => localize(value, lang);
  const bodyId = `pc-${platform.id}-body`;

  return (
    <div
      className={`ui-platform-card guideline-platform-card ${collapsed ? "is-collapsed" : ""}`}
      id={`pc-${platform.id}`}
    >
      <button
        type="button"
        className="ui-platform-head guideline-platform-head"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
      >
        <Tags
          size="guideline"
          variant="primary"
          appearance="soft"
          className="plat-logo ui-platform-logo"
        >
          {t(platform.logo)}
        </Tags>

        <span className="guideline-platform-title">
          <span className="ui-platform-name">{t(platform.name)}</span>
          <span className="ui-platform-subtitle">{t(platform.subtitle)}</span>
        </span>

        <span className="ui-platform-toggle" aria-hidden="true">
          <ChevronDown size={16} />
        </span>
      </button>

      <div className="ui-platform-body" id={bodyId} hidden={collapsed}>
        <div className="ui-portal-bar">
          <span className="ui-portal-text ui-inline-icon-text">
            <Globe2 className="guideline-inline-icon" size={16} />
            {t(platform.portalText)}
          </span>

          <a
            href={platform.portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ui-button outline soft sm ui-portal-button"
          >
            <span className="ui-button-icon">
              <Link size={18} />
            </span>
            <span className="ui-button-text">{t(LABELS.portalButton)}</span>
          </a>
        </div>

        {platform.meta && (
          <div className="ui-meta-grid">
            {platform.meta.map((item) => (
              <InfoItem
                key={t(item.label)}
                label={t(item.label)}
                value={t(item.value)}
                className={`${item.highlight ? "ui-info-item-highlight" : ""} ${
                  item.small ? "ui-info-item-small" : ""
                }`}
              />
            ))}
          </div>
        )}

        {platform.periods && (
          <>
            <MiniTitle icon={CalendarDays}>
              {t(platform.periodsTitle || LABELS.periods)}
            </MiniTitle>

            <div className="ui-tags-row">
              {platform.periods.map((period) => (
                <Tags key={t(period)} size="guideline" variant="warning" appearance="soft">
                  {t(period)}
                </Tags>
              ))}
            </div>
          </>
        )}

        {platform.note && (
          <NoticeBox
            variant={platform.note.variant}
            icon={platform.note.icon}
            className={`guideline guideline-alert ${platform.note.variant} ${
              platform.note.last ? "alert-last" : ""
            }`}
          >
            {t(platform.note.text)}
          </NoticeBox>
        )}

        {platform.contacts && (
          <>
            <div className="ui-section-divider" />

            <MiniTitle icon={Mail}>{t(LABELS.contacts)}</MiniTitle>

            <div className="ui-contact-row">
              {platform.contacts.map(({ icon: ContactIcon, text }) => (
                <Tags
                  key={t(text)}
                  size="guideline"
                  variant="neutral"
                  appearance="soft"
                  icon={<ContactIcon className="guideline-inline-icon" size={16} />}
                >
                  {t(text)}
                </Tags>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function DeliveryAppsGuideline({ lang }) {
  const ar = lang === "ar";
  const [collapsed, setCollapsed] = useState({});

  const togglePlatform = (id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div
      className={`section ${ar ? "arabic-section" : "english-section"} delivery-guideline`}
      id="payments"
    >
      <div className="ui-guideline-section">
        <SectionHeader
          icon={Rocket}
          title={ar ? "تطبيقات التوصيل" : "Delivery Apps"}
          description={
            ar
              ? "سياسات التحويل المالي وروابط بوابات كل تطبيق"
              : "Financial transfer policies and portal links for each app"
          }
        />

        <div className="guideline-platform-list">
          {PLATFORMS.map((platform) => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              lang={lang}
              collapsed={!!collapsed[platform.id]}
              onToggle={() => togglePlatform(platform.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
